import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import { useHistory, useRouteMatch } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath } from 'react-router-dom';
import { FastField, Field, Form, Formik } from 'formik';

import { Drawer, Grid, makeStyles } from '@material-ui/core';

import notification from 'app/config/notification';
import lineEndpoint from 'app/api/services/lineEndpoint';
import { errorHandler } from 'app/utils/errorHandler';
import { validateFormikForm } from 'app/utils/forms/functions';
import { contractInitialValues } from '../form-configs/contract-initial-values';
import { contractValidationSchema } from '../form-configs/validation-schemas';
import { clearOrgaos, fetchDefaultOrgao, fetchOrgaos } from '../../../store/slices/forms';

import { URLS } from 'app/config/urls';

import {
    clearContractType,
    fetchContractType,
    fetchLine,
    fetchPermissionaryRefData,
    fetchServicesType,
} from 'app/store/slices/line';

import Loading from 'app/layouts/Loading';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import CustomTextField from '../components/TextField';
import CustomDateField from '../components/DateField';
import InfoLineCompany from '../components/InfoLineCompany';
import ContractDocuments from '../components/ContractDocuments';

import styles from './styles';
import RequestProcess from '../components/RequestProcess';
import FindRequest from './drawers/FindRequest';
import { STATUS_CODES } from '../../../config/status';
const useStyles = makeStyles(styles);

const ContractDetails = (props) => {
    const history = useHistory();
    const match = useRouteMatch();
    const dispatch = useDispatch();
    const classes = useStyles();

    const cdLinhaFromPath = React.useRef(match.params.cdLinha).current;
    const cdContractFromPath = React.useRef(match.params.cdContratoTermoAditivo).current;
    const selectedRowIndex = React.useRef(-1);

    const selectedLineStore = useSelector((state) => state.line.selected);
    const orgaos = useSelector((state) => state.forms.orgaos);
    const defaultOrgao = useSelector((state) => state.forms.defaultOrgao);
    const contractType = useSelector((state) => state.line.contractType);
    const servicesType = useSelector((state) => state.line.servicesType);
    const permissionaryRefData = useSelector((state) => state.line.permissionaryRefData);
    const isStrip = useSelector((state) => state.line.isStrip);

    const [loading, setLoading] = useState(true);
    const [loadingLine, setLoadingLine] = useState(true);
    const [selectedContract, setSelectedContract] = useState(null);
    const [formInitials, setFormInitials] = useState(contractInitialValues);
    const [documents, setDocuments] = useState([]);
    const [deletedDocuments, setDeletedDocuments] = useState([]);
    const [processValues, setProcessValues] = useState({});
    const [openDrawer, setOpenDrawer] = useState(false);

    const canEdit = useMemo(() => {
        return true;
    }, []);

    useEffect(() => {
        return () => {
            dispatch(clearContractType());
            dispatch(clearOrgaos());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        (async () => {
            setLoadingLine(true);
            if (!selectedLineStore && cdLinhaFromPath) {
                await dispatch(fetchLine(cdLinhaFromPath));
            }
            setLoadingLine(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLineStore]);

    const getContractDataFromServer = async (id) => {
        const contractId = id || cdContractFromPath;
        const response = await lineEndpoint.getContract(contractId);
        if (response && response.status === 200) {
            const contract = { ...response.data };
            if (!contract.deRazaoSocial || contract.deRazaoSocial === '') {
                contract.deRazaoSocial =
                    selectedLineStore.empresa?.deRazaoSocial || selectedLineStore.empresa?.nmCompleto || '';
            }
            setSelectedContract(contract);
            let blockChangeRequest = [STATUS_CODES.CANCELADO, STATUS_CODES.APROVADO];
            let anexos = !contract.anexos
                ? []
                : contract.anexos.map((doc) => {
                      const temp = { ...doc };
                      temp.canEdit = true;
                      temp.lockSelect = true;
                      temp.error = {};
                      if (temp.solicitacao) {
                          temp.disabled = blockChangeRequest.includes(temp.solicitacao.cdSituacao);
                      }
                      if ((!temp.cdSolicitacaoAnterior || temp.cdSolicitacaoAnterior === '') && temp.cdSolicitacao) {
                          temp.cdSolicitacaoAnterior = temp.cdSolicitacao;
                      }
                      return temp;
                  });
            setDocuments(anexos);
            setFormInitials({ ...contractInitialValues, ...contract });
        }
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            const promisses = [];
            if (orgaos.length === 0) promisses.push(dispatch(fetchOrgaos()));
            if (Object.keys(defaultOrgao).length === 0) promisses.push(dispatch(fetchDefaultOrgao()));
            if (contractType.length === 0) promisses.push(dispatch(fetchContractType()));
            if (servicesType.length === 0) promisses.push(dispatch(fetchServicesType()));
            if (permissionaryRefData.length === 0) promisses.push(dispatch(fetchPermissionaryRefData()));
            if (promisses.length > 0) await Promise.all(promisses);
            try {
                if (cdContractFromPath) {
                    if (!selectedContract && selectedLineStore && defaultOrgao) {
                        await getContractDataFromServer();
                    }
                } else {
                    let tempInitialValues = { ...contractInitialValues, cdOrgaoTermoContrato: defaultOrgao?.cdOrgao };
                    tempInitialValues.deRazaoSocial =
                        selectedLineStore.empresa?.deRazaoSocial || selectedLineStore.empresa?.nmCompleto || '';
                    setFormInitials(tempInitialValues);
                }
                setLoading(false);
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cdContractFromPath, selectedLineStore, defaultOrgao]);

    const updateContract = (data) => {
        setSelectedContract(data);
        setFormInitials({ ...contractInitialValues, ...data });
    };

    const validateDocuments = () => {
        let error = false;
        if (documents.length > 0) {
            const tempDoc = documents.map((doc) => {
                const temp = _.cloneDeep(doc);
                if (!temp.boExcluido) {
                    if (!temp.nuAnexoContrato || temp.nuAnexoContrato === '') {
                        temp.error.nuAnexoContrato = 'Campo obrigatório';
                        error = true;
                    }
                    if (!temp.tipoDocumento || Object.keys(temp.tipoDocumento).length === 0) {
                        temp.error.tipoDocumento = 'Campo obrigatório';
                        error = true;
                    }
                }
                return temp;
            });
            setDocuments(tempDoc);
        }
        return !error;
    };

    const handleSubmitForm = async (values, formikHelpers) => {
        try {
            setLoading(true);
            if (!validateDocuments()) {
                notification('Informações obrigatórias nos anexos do contrato.', {
                    toastId: 'VALIDATION_DOCS',
                    type: 'error',
                });
                return;
            }

            let errors = 0;
            if (processValues.nuProcesso && processValues.nuProcesso !== '' && processValues.processError) {
                notification('O número do processo não existe no CPA', {
                    toastId: 'VALIDATION_CPA',
                    type: 'error',
                });
                if (formikHelpers) {
                    formikHelpers.setFieldError('nuProcesso', 'O número do processo não existe no CPA');
                }
                errors++;
            }
            if (errors > 0) return;

            const valuesCopy = _.cloneDeep(values);
            valuesCopy.cdLinha = selectedLineStore.cdLinha;
            valuesCopy.nuProcesso = processValues.nuProcesso;
            valuesCopy.cdProcesso = processValues.cdProcesso;
            valuesCopy.anexos = [...documents, ...deletedDocuments];

            const saved = await lineEndpoint.saveContract(valuesCopy);
            if (saved.status === 200) {
                notification('Contrato/termo com sucesso', {
                    toastId: `SUCCESS_SAVE_CONTRACT`,
                    type: 'success',
                });
                updateContract(saved.data);
                setDeletedDocuments([]);
                await getContractDataFromServer(saved.data.cdContratoTermoAditivo);

                if (!cdContractFromPath) {
                    let params = {
                        cdLinha: cdLinhaFromPath,
                        cdContratoTermoAditivo: saved.data.cdContratoTermoAditivo,
                    };
                    if (isStrip) params.isStrip = 'strip';
                    history.push(generatePath(URLS.LINES_MANAGE_CONTRACT_EDIT, params));
                }
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectRequestFromDrawer = (request) => {
        let index = selectedRowIndex.current;
        let value = request.nuSolicitacaoFmt;
        const invalidRequest =
            request.cdSituacao === STATUS_CODES.CANCELADO || request.cdSituacao === STATUS_CODES.APROVADO;

        let cdSolicitacao = null;
        let nuSolicitacaoFmt = null;
        let error = { ...documents[index].errors };

        if (!invalidRequest) {
            cdSolicitacao = request.cdSolicitacao;
            nuSolicitacaoFmt = value;
        } else {
            const msg = invalidRequest
                ? `A solicitação ${value} está cancelada ou aprovada`
                : `Solicitação ${value} não encontrada`;
            notification(msg, {
                toastId: 'VALIDATION',
                type: 'error',
            });
            error.nuSolicitacaoFmt = 'Campo incorreto';
        }

        setDocuments((prevDocs) => {
            let temp = [...prevDocs];
            temp[index] = { ...temp[index], cdSolicitacao, nuSolicitacaoFmt, error };
            return temp;
        });
    };

    return (
        <>
            <Formik
                enableReinitialize
                initialValues={formInitials}
                initialStatus={{ holding: false }}
                validationSchema={contractValidationSchema}
                validateOnChange={false}
                validateOnBlur={true}
                onSubmit={handleSubmitForm}>
                {(formProps) => {
                    const { handleSubmit, values } = formProps;
                    return (
                        <Form>
                            <PageWrapper title="Cadastro / Edição de Contrato, Termo de Permissão e Aditivo de Contrato">
                                {loading && <Loading />}
                                <Grid container spacing={3}>
                                    <Grid item container spacing={3} direction={'row'}>
                                        <Grid item xs={12} sm={6}>
                                            <FastField
                                                component={CustomTextField}
                                                label="Empresa"
                                                name="deRazaoSocial"
                                                inputProps={{
                                                    maxLength: 150,
                                                }}
                                            />
                                        </Grid>
                                        <InfoLineCompany
                                            selectedLine={selectedLineStore}
                                            loadingLine={loadingLine}
                                            hideEmpresa={true}
                                        />
                                    </Grid>
                                    <Grid item xs={12} container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Field
                                                component={CustomTextField}
                                                label="Número do processo SEI"
                                                name="nuProcessoSei"
                                                inputProps={{
                                                    maxLength: 30,
                                                }}
                                                validate={(value) => {
                                                    if (!value) {
                                                        const clearNuprocesso = processValues.nuProcesso
                                                            .trim()
                                                            .replace(/\D/g, '');
                                                        if (!clearNuprocesso) {
                                                            return 'Número de processo obrigatório';
                                                        }
                                                    }
                                                }}
                                                onBlur={() => {
                                                    formProps.setFieldTouched('nuProcesso');
                                                    formProps.setFieldTouched('nuProcessoSei');
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <RequestProcess
                                            {...props}
                                            {...formProps}
                                            initialState={formInitials}
                                            canEdit={canEdit}
                                            cdLinha={cdLinhaFromPath}
                                            requestProcessValues={processValues}
                                            setRequestProcessValues={setProcessValues}
                                            fieldWidth={12}
                                            hasRequest={false}
                                            nuProcessRequired={false}
                                            optionalSeiOrCpa={true}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            component={CustomTextField}
                                            label="Tipo de contrato"
                                            name="cdTipoContrato"
                                            select
                                            loading={loading}
                                            SelectProps={{
                                                native: true,
                                            }}>
                                            <option aria-label="None" value="">
                                                Selecione
                                            </option>
                                            {contractType?.map((entry, index) => {
                                                return (
                                                    <option key={`type-${index}`} value={entry.cdTipoContrato}>
                                                        {entry.deTipoContrato}
                                                    </option>
                                                );
                                            })}
                                        </Field>
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            component={CustomTextField}
                                            label="Orgão"
                                            name="cdOrgao"
                                            select
                                            loading={loading}
                                            SelectProps={{
                                                native: true,
                                            }}>
                                            <option aria-label="None" value="">
                                                Selecione
                                            </option>
                                            {orgaos?.map((entry, index) => {
                                                return (
                                                    <option key={`type-${index}`} value={entry.cdOrgao}>
                                                        {entry.sgOrgao}
                                                    </option>
                                                );
                                            })}
                                        </Field>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            component={CustomTextField}
                                            label="Tipo de serviço"
                                            name="cdTipoServico"
                                            select
                                            loading={loading}
                                            SelectProps={{
                                                native: true,
                                            }}>
                                            <option aria-label="None" value="">
                                                Selecione
                                            </option>
                                            {servicesType?.map((entry, index) => {
                                                return (
                                                    <option key={`type-${index}`} value={entry.cdTipoServico}>
                                                        {entry.deTipoServico}
                                                    </option>
                                                );
                                            })}
                                        </Field>
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <FastField
                                            component={CustomTextField}
                                            label="Número de contrato/termo"
                                            name="nuTermo"
                                            inputProps={{
                                                maxLength: 10,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Field
                                            component={CustomDateField}
                                            label="Data assinatura"
                                            name="dtAssinatura"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <Field
                                            component={CustomTextField}
                                            label="Prazo"
                                            name="nuPrazo"
                                            type="number"
                                            onChange={(e) => {
                                                let value = Number(e.target.value);
                                                if (value > 99.99) {
                                                    value = 99.99;
                                                }
                                                formProps.setFieldValue('nuPrazo', value);
                                            }}
                                            onBlur={(e) => {
                                                let value = Number(e.target.value);
                                                formProps.setFieldValue('nuPrazo', value.toFixed(2));
                                                formProps.handleBlur(e);
                                            }}
                                            inputProps={{
                                                maxLength: 10,
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={2}>
                                        <FastField
                                            component={CustomTextField}
                                            name="deTipoPrazo"
                                            select
                                            SelectProps={{
                                                native: true,
                                            }}>
                                            <option value="anos">Ano(s)</option>
                                            <option value="meses">Mes(es)</option>
                                        </FastField>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Field
                                            component={CustomTextField}
                                            label="A partir de"
                                            name="cdPermissionarioRefData"
                                            select
                                            loading={loading}
                                            disabled={loading}
                                            SelectProps={{
                                                native: true,
                                            }}>
                                            {permissionaryRefData.map((entry, index) => {
                                                return (
                                                    <option
                                                        key={`entry-${index}`}
                                                        value={entry.cdPermissionarioRefData}>
                                                        {entry.dePermissionarioRefData}
                                                    </option>
                                                );
                                            })}
                                        </Field>
                                    </Grid>

                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomDateField}
                                            label="Data inicio vigência"
                                            name="dtInicioVigencia"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomDateField}
                                            label="Data vencimento"
                                            name="dtVencimento"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Field
                                            component={CustomDateField}
                                            label="Data publicação"
                                            name="dtPublicacao"
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <FastField
                                            component={CustomTextField}
                                            label="Observação"
                                            name="deObservacao"
                                            multiline
                                            minRows={4}
                                            inputProps={{
                                                maxLength: 2000,
                                            }}
                                        />
                                    </Grid>

                                    <ContractDocuments
                                        {...formProps}
                                        classes={classes}
                                        selectedContract={selectedContract}
                                        documents={documents}
                                        setDocuments={setDocuments}
                                        deletedDocuments={deletedDocuments}
                                        setDeletedDocuments={setDeletedDocuments}
                                        onOpenRequestDrawer={({ open, index }) => {
                                            selectedRowIndex.current = index;
                                            setOpenDrawer(open);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <SaveBox
                                        onCancel={() => history.goBack()}
                                        onSave={() => {
                                            formProps.setFieldTouched('nuProcesso');
                                            formProps.setFieldTouched('nuProcessoSei');
                                            validateFormikForm(formProps, contractValidationSchema).then((res) => {
                                                if (Object.keys(res).length > 0) {
                                                    notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                                                        toastId: 'VALIDATION',
                                                        type: 'error',
                                                    });
                                                } else {
                                                    handleSubmit(values);
                                                }
                                            });
                                        }}
                                        firstButtonLabel={'VOLTAR'}
                                        middleButtonLabel={(() => {
                                            if (formProps.status.holding) {
                                                return 'Aguarde...';
                                            }
                                            if (canEdit) {
                                                return 'SALVAR';
                                            }
                                            return null;
                                        })()}
                                        middleButtonDisabled={formProps.status.holding}
                                    />
                                </Grid>
                            </PageWrapper>
                        </Form>
                    );
                }}
            </Formik>
            <Drawer anchor="right" open={openDrawer} onClose={() => setOpenDrawer(false)}>
                <FindRequest
                    onSave={handleSelectRequestFromDrawer}
                    onClose={() => setOpenDrawer(false)}
                    filters={{
                        situacoes: [
                            { cdSituacao: STATUS_CODES.EM_ANALISE },
                            { cdSituacao: STATUS_CODES.AGUARDANDO_AJUSTES_DOCUMENTO },
                            { cdSituacao: STATUS_CODES.RETORNO_DIRETOR_GERAL },
                        ],
                        servicos: [],
                    }}
                />
            </Drawer>
        </>
    );
};

export default ContractDetails;
